import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Menu, X, ArrowRight, ChevronDown, ShoppingBag } from 'lucide-react';
import PWAInstallButton from './PWAInstallButton';

const MENUS = [
  {
    label: 'Reisen',
    image: '/nav/reisen-480.webp',
    imageStyle: 'editorial',
    imageBadge: 'REISEN',
    imageTitle: '1,6 Mio Hotels.\nWeltweit.',
    imageCaption: 'Hotels und Unterkünfte mit automatischer FuelGood-Verrechnung.',
    sections: [
      {
        title: 'Privatreisen',
        links: [
          { label: 'Hotel buchen', to: '/hotel-suche', desc: '1,6 Mio Hotels · Bestpreis-Garantie' },
          { label: 'FuelX Hotels', to: '/fuelx-hotels', desc: '250k Luxushotels für mehr Ersparnis' },
          { label: 'Hotel-Dienstleister', to: '/hotel-partner', desc: '40+ Hotelketten weltweit' },
        ],
      },
      {
        title: 'Geschäftsreisen',
        links: [
          { label: 'Business Travel', to: '/business/geschaeftsreisen', desc: 'Dienstreisen über Gold Card' },
          { label: 'Buchungen verwalten', to: '/dashboard', desc: 'Anfragen & Status im Dashboard' },
        ],
      },
    ],
  },
  {
    label: 'Auto-Services',
    image: '/nav/auto-services-480.webp',
    imageStyle: 'editorial',
    imageBadge: 'AUTO-SERVICES',
    imageTitle: 'Alle Auto-Services.\nEin Konto.',
    imageCaption: 'Verkauf, Kredit, Versicherung, TÜV, Steuer, Werkstatt, Öl, Reifen und Pannenhilfe — aus einer Hand.',
    sections: [
      {
        title: 'Verkauf & Finanzierung',
        links: [
          { label: 'Auto verkaufen', to: '/auto-verkaufen', desc: 'Rundum-Service · 2 Jahre Käufer-Garantie' },
          { label: 'Autokredit', to: '/autokredit', desc: '20+ Banken im Vergleich' },
          { label: 'KFZ-Versicherung', to: '/versicherung', desc: 'KI-Check · Angebot direkt vom Versicherer' },
          { label: 'KFZ-Steuer', to: '/kfz-steuer', desc: 'Bis 450 € über FuelX Hotels gutschreibbar' },
        ],
      },
      {
        title: 'Werkstatt & Wartung',
        links: [
          { label: 'Pannenhilfe', to: '/pannenhilfe', desc: 'Bis 250 € · ADAC, ACE, AvD & Co.' },
          { label: 'Öl-Wechsel', to: '/oel-service', desc: 'Bis 450 € Wartung gutschreibbar' },
          { label: 'Reifen-Wechsel', to: '/reifen-wechsel', desc: 'Bis 250 € Reifen-Kosten' },
          { label: 'TÜV-Service', to: '/tuev-service', desc: 'Bis 160 € HU/AU auf Prepaid Card' },
        ],
      },
    ],
  },
  {
    label: 'Business',
    image: '/nav/business-480.webp',
    imageStyle: 'editorial',
    imageBadge: 'BUSINESS',
    imageTitle: 'Flotte.\nOhne Reibung.',
    imageCaption: 'Ein Portal für Reisen, Auto-Services & Flottenmanagement — Ihr Team, unser Concierge.',
    sections: [
      {
        title: 'Fuhrpark & Kosten',
        links: [
          { label: 'Fuhrpark-Optimierung', to: '/business/fuhrpark', desc: 'Zentrale Kostenkontrolle · Versicherungs-Check' },
          { label: 'Flottenvorteile in €', to: '/business/flottenvorteile', desc: 'Bis 5.000 € Vorteile pro Fahrzeug' },
          { label: 'Verkauf & Leasing', to: '/business/leasing-service', desc: 'Marktpreis · Rückgabe · Wertgutachten' },
        ],
      },
      {
        title: 'Reisen & Mitarbeiter',
        links: [
          { label: 'Geschäftsreisen', to: '/business/geschaeftsreisen', desc: 'Dienstreisen · 1,6 Mio Hotels weltweit' },
          { label: 'Mitarbeiter-Benefit', to: '/business/mitarbeiter-benefit', desc: 'Reisevorteile auch für private Buchungen' },
          { label: 'Einsparpotenzial berechnen', to: '/business/rechner', desc: 'Rechner: Wirkung auf Ihre Flottengröße' },
        ],
      },
    ],
  },
  {
    label: 'Vorteile',
    image: '/nav/vorteile-480.webp',
    imageStyle: 'editorial',
    imageBadge: 'VORTEILE',
    imageTitle: '37 Leistungen.\nEin Konto.',
    imageCaption: 'Deine Kfz-Kosten senken · Service bündeln · FuelGood-Prämien freischalten.',
    sections: [
      {
        title: 'Entdecken',
        links: [
          { label: 'Alle 37 Leistungen', to: '/vorteile', desc: 'Mit FuelX vs. ohne — direkter Vergleich' },
          { label: 'Silver vs. Gold', to: '/vergleich', desc: 'Karten im Direktvergleich' },
          { label: 'Vorteilsrechner', to: '/sparen', desc: 'Deine persönliche Ersparnis' },
          { label: 'Kilometer-Rechner', to: '/kilometer-rechner', desc: 'FuelGood pro km live berechnen' },
        ],
      },
      {
        title: 'Konditionen',
        links: [
          { label: 'Preise', to: '/preise', desc: 'Silver ab 99,90 € · Gold 49,90 € / Fahrzeug·Jahr' },
          { label: 'So funktioniert\'s', to: '/so-funktionierts', desc: 'Fahren → FuelGood → Vorteile einlösen' },
          { label: 'FAQ', to: '/faq', desc: 'Antworten zu Card, Guthaben & Vorteilen' },
        ],
      },
    ],
  },
  {
    label: 'FUEL.X Card',
    to: '/fuelx-card',
  },
];

export default function Header() {
  const { isAuthenticated } = useAuth();
  const { openCart, hasItems } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 bg-white"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="global-header-wrap"
    >
      {/* Trust Top-Bar — First-Mover-Claim */}
      <div className="bg-white text-stone-600 border-b border-stone-200" data-testid="trust-topbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-7 flex items-center justify-between text-[10px] tracking-wide">
          <div className="flex items-center gap-2 font-medium">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="uppercase tracking-[0.2em] text-[10px] text-stone-700 whitespace-nowrap">
              <span className="hidden sm:inline">Die Tankkarte für Autofahrer · Privat &amp; Geschäftlich</span>
              <span className="sm:hidden">Die Tankkarte für Autofahrer</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-4 text-stone-400 text-[10px] tracking-wide">
            <span>★ 4,7 Kundenbewertung</span>
            <span className="hidden lg:inline">· Shell · Aral · BP · Total</span>
          </div>
          <div className="flex md:hidden items-center gap-1.5 text-stone-400">
            <span>★ 4,7</span>
          </div>
        </div>
      </div>
      <header
        className="bg-white/95 backdrop-blur-xl border-b border-stone-100"
        data-testid="global-header"
        onMouseLeave={() => setActiveMenu(null)}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img loading="lazy" decoding="async" src="/fuelx-logo-final-cropped-96.webp" alt="FuelX – Autoplattform und FuelX Card" width="891" height="216" className="h-5 sm:h-6 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {MENUS.map((menu, idx) => (
              menu.to ? (
                <Link
                  key={idx}
                  to={menu.to}
                  onMouseEnter={() => setActiveMenu(null)}
                  className="px-3.5 py-2 rounded-full text-sm text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                  data-testid={`nav-link-${idx}`}
                >
                  {menu.label}
                </Link>
              ) : (
              <div
                key={idx}
                className="relative"
                onMouseEnter={() => setActiveMenu(idx)}
              >
                <button
                  className={`px-3.5 py-2 rounded-full text-sm flex items-center gap-1 transition-colors ${
                    activeMenu === idx ? 'bg-stone-100 text-stone-900' : 'text-stone-500 hover:text-stone-900'
                  }`}
                  data-testid={`nav-menu-${idx}`}
                >
                  {menu.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeMenu === idx ? 'rotate-180' : ''}`} strokeWidth={2} />
                </button>
              </div>
              )
            ))}
          </nav>

          {/* Desktop + Tablet CTA */}
          <div className="hidden md:flex items-center gap-1.5 sm:gap-3">
            <PWAInstallButton variant="light" label="App" testId="header-pwa-install" className="!py-1.5 !text-[11px]" />
            <Link
              to="/warenkorb"
              className="relative w-9 h-9 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
              aria-label="Warenkorb öffnen"
              data-testid="header-cart-btn"
            >
              <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.8} />
              {hasItems && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" data-testid="header-cart-badge" />
              )}
            </Link>
            {isAuthenticated ? (
              <button onClick={() => navigate('/dashboard')} className="px-4 md:px-6 py-2 md:py-2.5 rounded-full bg-stone-900 text-white text-xs md:text-sm font-medium hover:bg-stone-800 transition-colors" data-testid="header-dashboard-btn">
                Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="px-3 md:px-4 py-2 text-xs md:text-sm text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap" data-testid="header-login-btn">
                  Anmelden
                </button>
                <button onClick={() => openCart()} className="px-4 md:px-6 py-2 md:py-2.5 rounded-full bg-stone-900 text-white text-xs md:text-sm font-medium hover:bg-stone-800 transition-colors flex items-center gap-1.5 md:gap-2 whitespace-nowrap" data-testid="header-register-btn">
                  Card bestellen <ArrowRight className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2} />
                </button>
              </>
            )}
          </div>

          {/* Mobile: kompakter Quick-CTA — immer sichtbar */}
          <div className="flex md:hidden items-center gap-0.5 flex-shrink min-w-0">
            {hasItems && (
              <Link
                to="/warenkorb"
                className="relative w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors flex-shrink-0"
                aria-label="Warenkorb öffnen"
                data-testid="header-mobile-cart-btn"
              >
                <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.8} />
                <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
              </Link>
            )}
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 rounded-full bg-stone-900 text-white text-[12px] font-semibold hover:bg-stone-800 transition-colors whitespace-nowrap inline-flex items-center gap-1"
                data-testid="header-mobile-dashboard-btn"
              >
                Dashboard <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="px-1.5 py-2 text-[12px] font-medium text-stone-600 hover:text-stone-900 transition-colors whitespace-nowrap" data-testid="header-mobile-login-btn">
                  Login
                </button>
                <button onClick={() => openCart()} className="px-2.5 py-2 rounded-full bg-stone-900 text-white text-[11px] font-semibold hover:bg-stone-800 transition-colors whitespace-nowrap" data-testid="header-mobile-cta-btn">
                  Card bestellen
                </button>
              </>
            )}
            <button className="p-1 -mr-0.5 text-stone-700 flex-shrink-0" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menü öffnen" data-testid="header-mobile-toggle">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mega Dropdown */}
      {activeMenu !== null && MENUS[activeMenu] && MENUS[activeMenu].sections && (
        <div className="hidden lg:block absolute top-full left-0 right-0 bg-white border-b border-stone-200 shadow-lg" data-testid="mega-menu" onMouseLeave={() => setActiveMenu(null)}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
            <div className="grid grid-cols-[1.1fr_auto] gap-12">
              {/* Link sections */}
              <div className={`grid gap-12 ${MENUS[activeMenu].sections.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {MENUS[activeMenu].sections.map((section, si) => (
                  <div key={si}>
                    {section.title && <p className="text-xs font-medium uppercase tracking-[0.15em] text-stone-400 mb-4">{section.title}</p>}
                    <div className="space-y-1">
                      {section.links.map((link, li) => (
                        <Link
                          key={li}
                          to={link.to}
                          onClick={() => setActiveMenu(null)}
                          className="block px-3 py-2.5 -mx-3 rounded-xl hover:bg-stone-50 transition-colors group"
                          data-testid={`mega-link-${link.to.replace(/\//g, '-')}`}
                        >
                          <span className="block text-sm font-medium text-stone-900 group-hover:text-stone-700">{link.label}</span>
                          <span className="block text-xs mt-0.5 text-stone-400">{link.desc}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Thematic image — 420 px, natürliches 16:9 Format ohne Crop (User-Wunsch: Model muss ganz zu sehen sein) */}
              {MENUS[activeMenu].image && (
                <div className="hidden lg:block w-[420px] flex-shrink-0">
                  <div
                    className="relative rounded-2xl overflow-hidden aspect-[16/9] bg-stone-900"
                    data-testid="mega-menu-image"
                  >
                    <img
                      src={MENUS[activeMenu].image}
                      alt={MENUS[activeMenu].label}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {/* Editorial-Overlay: sanfter Verlauf unten für Text-Lesbarkeit */}
                    <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/25 to-transparent pointer-events-none" />
                    <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[9px] font-medium uppercase tracking-[0.25em] text-white/80">FUEL.X</span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <h3 className="text-white text-lg font-medium tracking-tight leading-[1.1] whitespace-pre-line">
                        {MENUS[activeMenu].imageTitle || MENUS[activeMenu].label}
                      </h3>
                      {MENUS[activeMenu].imageCaption && (
                        <p className="text-[10.5px] text-white/70 leading-relaxed max-w-full mt-2 line-clamp-2">
                          {MENUS[activeMenu].imageCaption}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100 max-h-[80vh] overflow-y-auto" data-testid="header-mobile-menu">
          <div className="px-6 py-4">
            {MENUS.map((menu, mi) => (
              <div key={mi}>
                {menu.to ? (
                  <Link
                    to={menu.to}
                    onClick={() => setMobileOpen(false)}
                    className="block py-4 text-[16px] font-medium border-b border-stone-100 text-stone-900"
                    data-testid={`mobile-nav-${mi}`}
                  >
                    {menu.label}
                  </Link>
                ) : (
                  <details className="group border-b border-stone-100">
                    <summary className="flex items-center justify-between py-4 text-[16px] font-medium text-stone-900 list-none cursor-pointer select-none" data-testid={`mobile-nav-${mi}`}>
                      {menu.label}
                      <ChevronDown className="w-4 h-4 text-stone-400 transition-transform group-open:rotate-180" strokeWidth={2} />
                    </summary>
                    <div className="pb-3">
                      {menu.sections.flatMap((s) => s.links).map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block py-3 pl-3 text-[15px] text-stone-600 border-b border-stone-50 last:border-0">
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
            <div className="border-t border-stone-100 pt-4 space-y-2">
              {isAuthenticated ? (
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block w-full text-center px-6 py-3 rounded-full bg-stone-900 text-white text-sm font-medium">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-center text-sm text-stone-500 py-2">Anmelden</Link>
                  <button
                    type="button"
                    onClick={() => { setMobileOpen(false); openCart(); }}
                    className="block w-full text-center px-6 py-3 rounded-full bg-stone-900 text-white text-sm font-medium"
                    data-testid="mobile-menu-order-btn"
                  >
                    Card bestellen
                  </button>
                </>
              )}
              <div className="flex justify-center pt-1">
                <PWAInstallButton variant="light" label="Als App installieren" testId="mobile-menu-pwa-install" />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
    </div>
  );
}
